import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const AcademicBackgroundStep = ({ data, onUpdate, onNext, onPrevious, errors, setErrors }) => {
  const [hasWorkExperience, setHasWorkExperience] = useState(data.hasWorkExperience || false);

  const qualificationOptions = [
    { value: 'btech', label: 'B.Tech/B.E.' },
    { value: 'bca', label: 'BCA (Bachelor of Computer Applications)' },
    { value: 'bsc', label: 'B.Sc. (Computer Science/IT)' },
    { value: 'mtech', label: 'M.Tech/M.E.' },
    { value: 'mca', label: 'MCA (Master of Computer Applications)' },
    { value: 'msc', label: 'M.Sc. (Computer Science/IT)' },
    { value: 'diploma', label: 'Diploma in Computer Science/IT' },
    { value: 'other', label: 'Other' }
  ];

  const graduationYearOptions = Array.from({ length: 10 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return { value: year.toString(), label: year.toString() };
  });

  const skillsOptions = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'python', label: 'Python' },
    { value: 'java', label: 'Java' },
    { value: 'react', label: 'React.js' },
    { value: 'nodejs', label: 'Node.js' },
    { value: 'html', label: 'HTML/CSS' },
    { value: 'sql', label: 'SQL' },
    { value: 'mongodb', label: 'MongoDB' },
    { value: 'git', label: 'Git/GitHub' },
    { value: 'aws', label: 'AWS' },
    { value: 'docker', label: 'Docker' },
    { value: 'linux', label: 'Linux' },
    { value: 'networking', label: 'Networking' },
    { value: 'troubleshooting', label: 'Troubleshooting' },
    { value: 'hardware', label: 'Hardware Support' }
  ];

  const experienceYearOptions = [
    { value: '0-1', label: '0-1 years' },
    { value: '1-2', label: '1-2 years' },
    { value: '2-3', label: '2-3 years' },
    { value: '3-5', label: '3-5 years' },
    { value: '5+', label: '5+ years' }
  ];

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleWorkExperienceToggle = (checked) => {
    setHasWorkExperience(checked);
    onUpdate({ 
      hasWorkExperience: checked,
      ...(checked ? {} : {
        experienceYears: '',
        previousCompany: '',
        previousRole: ''
      })
    });
  };

  const validateStep = () => {
    const newErrors = {};
    
    if (!data.qualification) newErrors.qualification = 'Qualification is required';
    if (!data.graduationYear) newErrors.graduationYear = 'Graduation year is required';
    if (!data.collegeName?.trim()) newErrors.collegeName = 'College/University name is required';
    // if (!data.technicalSkills || data.technicalSkills.length === 0) {
    //   newErrors.technicalSkills = 'Please select at least one technical skill';
    // }

    if (hasWorkExperience) {
      if (!data.experienceYears) newErrors.experienceYears = 'Experience duration is required';
      if (!data.previousCompany?.trim()) newErrors.previousCompany = 'Previous company name is required';
      if (!data.previousRole?.trim()) newErrors.previousRole = 'Previous role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      onNext();
    }
  };

  return (
    <div className="space-y-8">
      {/* Educational Background Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-lg">
            <Icon name="GraduationCap" size={18} color="var(--color-primary)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Educational Background</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Highest Qualification"
            placeholder="Select your qualification"
            options={qualificationOptions}
            value={data.qualification || ''}
            onChange={(value) => handleInputChange('qualification', value)}
            error={errors.qualification}
            required
          />

          <Select
            label="Graduation Year"
            placeholder="Select graduation year"
            options={graduationYearOptions}
            value={data.graduationYear || ''}
            onChange={(value) => handleInputChange('graduationYear', value)}
            error={errors.graduationYear}
            required
          />

          <div className="md:col-span-2">
            <Input
              label="College/University Name"
              type="text"
              placeholder="Enter your college or university name"
              value={data.collegeName || ''}
              onChange={(e) => handleInputChange('collegeName', e.target.value)}
              error={errors.collegeName}
              required
            />
          </div>
        </div>
      </div>

      {/* Technical Skills Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-secondary/10 rounded-lg">
            <Icon name="Code" size={18} color="var(--color-secondary)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Technical Skills</h3>
        </div>

        <Select
          label="Technical Skills"
          placeholder="Select your technical skills"
          description="Choose all skills you are familiar with"
          options={skillsOptions}
          value={data.technicalSkills || []}
          onChange={(value) => handleInputChange('technicalSkills', value)}
          error={errors.technicalSkills}
          multiple
          searchable
        />
      </div>

      {/* Work Experience Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-accent/10 rounded-lg">
            <Icon name="Briefcase" size={18} color="var(--color-accent)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Work Experience</h3>
        </div>

        <div className="space-y-6">
          <Checkbox
            label="I have work experience"
            description="Check this if you have any professional work experience"
            checked={hasWorkExperience}
            onChange={(e) => handleWorkExperienceToggle(e.target.checked)}
          />

          {hasWorkExperience && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-muted/50 rounded-lg border border-border">
              <Select
                label="Years of Experience"
                placeholder="Select experience duration"
                options={experienceYearOptions}
                value={data.experienceYears || ''}
                onChange={(value) => handleInputChange('experienceYears', value)}
                error={errors.experienceYears}
                required
              />

              <Input
                label="Previous Company"
                type="text"
                placeholder="Enter company name"
                value={data.previousCompany || ''}
                onChange={(e) => handleInputChange('previousCompany', e.target.value)}
                error={errors.previousCompany}
                required
              />

              <div className="md:col-span-2">
                <Input
                  label="Previous Role/Position"
                  type="text"
                  placeholder="Enter your previous job title"
                  value={data.previousRole || ''}
                  onChange={(e) => handleInputChange('previousRole', e.target.value)}
                  error={errors.previousRole}
                  required
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Information Section */}
      <div className="bg-surface rounded-lg border border-border p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="flex items-center justify-center w-8 h-8 bg-success/10 rounded-lg">
            <Icon name="FileText" size={18} color="var(--color-success)" strokeWidth={2} />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Additional Information</h3>
        </div>

        <Input
          label="Additional Skills or Certifications (Optional)"
          type="text"
          placeholder="Any other relevant skills, certifications, or achievements"
          value={data.additionalInfo || ''}
          onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
          description="Include any certifications, online courses, or projects"
        />
      </div>

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4 pt-6">
        <Button
          variant="outline"
          onClick={onPrevious}
          iconName="ChevronLeft"
          iconPosition="left"
          className="sm:w-auto w-full"
        >
          Previous
        </Button>

        <div className="flex space-x-4">
          <Button
            variant="ghost"
            iconName="Save"
            iconPosition="left"
            className="sm:w-auto w-full"
          >
            Save Draft
          </Button>

          <Button
            variant="default"
            onClick={handleNext}
            iconName="ChevronRight"
            iconPosition="right"
            className="sm:w-auto w-full"
          >
            Next Step
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AcademicBackgroundStep;